import { container } from "tsyringe";
import JsBarcode from "jsbarcode";
import { PrintDetailRequest } from "@/data/payload/api/BookingRequest";
import { PrintDataEntitas } from "@/domain/entities/Booking";
import { FlagsPermissionBooking } from "@/feature-flags/flag-booking";
import { FlagsPermissionBulkPrint } from "@/feature-flags/flag-bulk-print";
import { AccountController } from "../AccountController";
import { BookingPresenter } from "../../presenters/BookingPresenter";
import generateThermalPDF from "../../views/shipment/bulk-print-resi/modules/bulk-label-resi-thermal";
import generatePDF from "../../views/shipment/bulk-print-resi/modules/bulk-label-resi-pdf";
import {
  assertRelabelPrintLimit,
  loadRelabelPrintData,
  relabelPrintLimit,
  RelabelPrintSize
} from "./relabel-print";

export interface RelabelPrintRequest {
  size: RelabelPrintSize;
  sttNos: string[];
  sttNo?: string;
}

function barcodeSource(value: string): string {
  if (!value) return "";
  const image = document.createElement("img");
  JsBarcode(image, value, {
    displayValue: false,
    textAlign: "left",
    fontSize: 16
  });
  return image.src;
}

export function createRelabelPrintPieces(
  item: PrintDataEntitas
): PrintDataEntitas[] {
  if (!item?.sttPieces?.length)
    throw new Error("Data koli resi tidak tersedia.");
  const barcode = barcodeSource(item.sttNo);
  const vendorRefCode = barcodeSource(item.sttVendorRefferenceNo);
  return item.sttPieces.map((piece) => ({
    ...piece,
    ...item,
    barcode,
    vendorRefCode
  }));
}

async function getRelabelPrintStts(
  request: RelabelPrintRequest
): Promise<string[]> {
  if (request.sttNo) return [request.sttNo];
  const cap = relabelPrintLimit(
    FlagsPermissionBulkPrint.config_max_stt_input_bulk_print.getValue()
  );
  if (request.sttNos.some((sttNo) => !sttNo))
    throw new Error("Nomor STT Relabel tidak tersedia.");
  const stts = Array.from(new Set(request.sttNos));
  assertRelabelPrintLimit(stts.length, cap);
  return stts;
}

export async function printRelabel(
  request: RelabelPrintRequest
): Promise<string[]> {
  if (!FlagsPermissionBooking.config_is_new_endpont_detail_stt.isEnabled()) {
    throw new Error("Endpoint print STT v2 belum aktif.");
  }
  const account = AccountController.accountData;
  const accountType =
    account.account_type === "partner"
      ? account.account_type_detail.type
      : account.account_type;
  const presenter = container.resolve(BookingPresenter);
  const stts = await getRelabelPrintStts(request);
  if (!stts.length) throw new Error("Tidak ada STT untuk dicetak.");
  if (request.sttNo) {
    const data = await presenter.getPrintDataByStt(
      new PrintDetailRequest({ sttNo: request.sttNo, id: 0, isBulk: false, accountType })
    );
    const book: any = await import(
      "@/app/ui/views/shipment/booking/create-booking/payment-detail/book.vue"
    );
    await book.default.methods.generateReceipt(
      data,
      book.default.methods.bookingTypePayload(data),
      data.sttShipmentId,
      request.size === "A6" ? 0 : 1,
      true,
      ["INTERPACK", "INTERPACKMAXI"].includes(data.sttProductType) ? "en" : "id",
      false
    );
    return [];
  }
  const result = await loadRelabelPrintData(stts, async (sttNo) => {
    const data = await presenter.getPrintDataByStt(
      new PrintDetailRequest({
        sttNo,
        id: 0,
        isBulk: !request.sttNo,
        accountType
      })
    );
    return createRelabelPrintPieces(data);
  });
  const pieces = result.successful.reduce<PrintDataEntitas[]>(
    (all, items) => all.concat(items),
    []
  );
  if (!pieces.length)
    throw new Error(`Gagal mengambil resi: ${result.failed.join(", ")}`);
  if (request.size === "A6")
    await generateThermalPDF(account.account_type, pieces, [], false);
  else await generatePDF(account.account_type, pieces, []);
  return result.failed;
}
